// common/useSavedFilters.js
// Сохранение URL страницы целиком (путь + query) по кнопке, с учётом страницы и пространства.
//
// Использование (pageKey обязателен, у каждой страницы свой):
//   useSavedFilters(selectedSpace?.value, 'products-list')
//   useSavedFilters(selectedSpace?.value, 'orders')
//
// Правила:
//  - пока кнопку не нажали, ничего не пишется, страница работает как обычно
//  - при каждой смене страницы/пути, если есть сохранённое под этим ключом, всегда
//    перенаправляем на него (replace), даже если в URL уже есть query
//  - период (date_from/date_to) не сохраняется и при подстановке не трогается
//  - исключение: заход по внешней ссылке с query. Пока путь тот же, ссылка важнее сохранённого
//  - у каждой страницы своя запись: ключ = pageKey + пространство
//
// isReady — true, когда URL готов: сохранённый уже подставлен или сохранённого нет.
// Если у новой страницы есть сохранённое, isReady на время становится false, пока оно не
// подставлено в URL (страница по этому переходу заново применяет URL и перезапрашивает фильтры).
// Если сохранённого нет, isReady не меняется: переход на такую страницу ничего не перезапускает.
// Стартовые шаги страницы ждут isReady.

import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePushNotifications } from './pushNotification';

// Ключ зависит от страницы и пространства: фильтры принадлежат конкретной странице и space
const getStorageKey = (pageKey, space) => `saved-filters:v1:${pageKey}:${space ?? 'default'}`;

// Часть URL после пути: «?query#hash», либо пустая строка
const getTail = (url) => {
  const i = url.search(/[?#]/);
  return i === -1 ? '' : url.slice(i);
};

// Есть ли в хвосте query (а не только hash)
const hasQuery = (url) => /\?[^#]+/.test(getTail(url));

// «?query#hash» без date_from/date_to (период не сохраняем).
// Строку не пересобираем через URLSearchParams, чтобы не изменить кодирование параметров.
const stripDates = (tail) => {
  const [query, hash = ''] = tail.split(/(?=#)/);
  const kept = query.replace(/^\?/, '').split('&').filter((p) => p && !/^date_(from|to)=/.test(p));
  return `${kept.length ? `?${kept.join('&')}` : ''}${hash}`;
};

const loadSavedUrl = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch {
    return '';
  }
};

export const useSavedFilters = (space, pageKey) => {
  const { search, pathname, hash } = useLocation();
  const navigate = useNavigate();
  const { addErrorPush, addSuccessPush } = usePushNotifications();

  if (!pageKey) {
    console.warn('[useSavedFilters] pageKey не передан: фильтры разных страниц будут смешиваться');
  }
  const storageKey = getStorageKey(pageKey ?? 'unknown', space);

  // Ждём, пока пространство загрузится: без него нет верного ключа хранилища,
  // а запросы фильтров уходят с пустым пространством и не повторяются.
  const hasSpace = space !== undefined && space !== null;

  // Но не бесконечно: если пространство не появилось за секунду, продолжаем без него
  // (ключ хранилища будет с «default»), чтобы страница не зависла.
  const [spaceTimedOut, setSpaceTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSpaceTimedOut(true), 1000);
    return () => clearTimeout(t);
  }, []);
  const canRead = hasSpace || spaceTimedOut;

  // Сохранённое под текущим ключом: «?query#hash» или '' (нет). Считается сразу при рендере,
  // поэтому на новой странице известно с первого рендера. version обновляет значение после
  // «Сохранить»/«Очистить».
  const [version, setVersion] = useState(0);
  const savedTail = useMemo(() => {
    // stripDates и здесь: в старых записях период мог быть сохранён
    const tail = stripDates(getTail(loadSavedUrl(storageKey)));
    return hasQuery(tail) ? tail : '';
  }, [storageKey, version]);
  const hasSavedFilters = !!savedTail;

  // Внешняя ссылка: заход сразу с query. Пока пользователь остаётся на этом пути,
  // сохранённое её не перебивает; как только путь сменился (другая страница/вкладка),
  // обычное правило «всегда перенаправлять на сохранённое».
  const externalPathRef = useRef(search ? pathname : null);
  if (externalPathRef.current !== null && pathname !== externalPathRef.current) {
    externalPathRef.current = null;
  }
  const isExternal = externalPathRef.current !== null;

  // Проверяем при заходе и при каждой смене страницы/пространства/пути.
  // Если под этим ключом есть сохранённое, всегда перенаправляем на него (replace),
  // даже если в URL уже есть другой query.
  // Смена одного только query (выбор фильтров, «Сбросить», «Сохранить») проверку не запускает,
  // поэтому после сброса фильтры сами не возвращаются.
  useEffect(() => {
    if (!canRead || !savedTail || isExternal) return;

    // Применяем только query/hash к ТЕКУЩЕМУ пути: ключ определяет страницу,
    // а не путь из записи, поэтому увести на другую страницу не может.
    // replace, чтобы «назад» не возвращало на прежний URL; если URL уже такой же
    // (без учёта периода), не навигируем. Период страница сама вернёт в URL из своего состояния.
    if (savedTail !== stripDates(`${search}${hash}`)) {
      navigate(`${pathname}${savedTail}`, { replace: true });
    }
  }, [canRead, storageKey, pathname]);

  // Готово: пространство известно и (если есть сохранённое) оно уже в URL.
  // Не зависит от того, когда router применит navigate.
  // Защёлка по ключу: для текущей страницы, став true, остаётся true (иначе после «Сбросить»
  // query пустой и isReady мог бы снова стать false, а потом true, перезапустив стартовые шаги).
  // Если сохранённого нет, isReady true сразу и при смене страницы не меняется.
  const readyKeyRef = useRef(null);
  if (canRead && (!savedTail || isExternal || stripDates(search) === savedTail.split('#')[0])) {
    readyKeyRef.current = storageKey;
  }
  const isReady = canRead && readyKeyRef.current === storageKey;

  const [isSaved, setIsSaved] = useState(false);
  const timerRef = useRef();

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Кнопка «Сохранить фильтры»: записывает текущий URL (путь + query + hash) без периода
  // (date_from/date_to). Домен не сохраняем: он разный на разных окружениях,
  // а navigate принимает путь.
  const saveFilters = () => {
    const tail = stripDates(`${search}${hash}`);
    if (!hasQuery(tail)) return;
    try {
      localStorage.setItem(storageKey, `${pathname}${tail}`);
    } catch {
      return; // приватный режим / переполнение / заблокированное хранилище
    }
    setIsSaved(true);
    setVersion((v) => v + 1);

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setIsSaved(false)
      addSuccessPush('Фильтры для этой страницы сохранены на вашем компьютере')
    }, 1000);
  };

  const clearSavedFilters = () => {
    try {
      localStorage.removeItem(storageKey);
      setVersion((v) => v + 1);
      addSuccessPush('Фильтры для этой страницы очищены')
    } catch {
      // ignore
    }
  };

  return { isReady, saveFilters, clearSavedFilters, isSaved, hasSavedFilters };
};
